// Géométrie de la couverture qui se tourne (CoverPageTurn) : un pli droit part du
// coin bas droit et balaie la page jusqu'au dos du magazine, à gauche. Au-delà du
// pli, la couverture se rabat par-dessus elle-même et montre son verso ; en deçà,
// la page intérieure apparaît. Fonctions pures, vérifiables sans navigateur.

const TILT = 0.78; // inclinaison du pli au départ (≈ 45°) : c'est le coin qui se soulève d'abord
const LIFT = 0.62; // au milieu du geste, le rabat se soulève (≈ 35°) : on le voit en raccourci

/** Avancement du geste, de 0 (au repos, coin corné) à 1 (couverture tournée), lent aux deux bouts. */
export function turnProgress(scrolled, length) {
  if (length <= 0) return 1;
  const x = Math.min(1, Math.max(0, scrolled / length));
  return 0.5 - Math.cos(Math.PI * x) / 2;
}

/**
 * Le pli pour un avancement p, sur une page width × height. Il coupe le bas de la page
 * en `origin` et penche vers la droite en remontant (angle `tilt` depuis la verticale) :
 * le bas de la page tourne avant le haut. `normal` pointe vers la partie rabattue,
 * `lift` est le raccourci du rabat soulevé (1 : à plat). Au repos, seul le coin est
 * corné, sur `ear` pixels.
 */
export function foldAt(p, width, height, ear) {
  const tilt = TILT * Math.pow(1 - p, 1.5);
  return {
    tilt,
    origin: { x: (width - ear) * (1 - p), y: height },
    along: { x: Math.sin(tilt), y: -Math.cos(tilt) },
    normal: { x: Math.cos(tilt), y: Math.sin(tilt) },
    lift: Math.cos(LIFT * Math.sin(Math.PI * p)),
  };
}

/**
 * Le pli de la quatrième de couverture qui se referme (BackCoverTurn), pour un avancement
 * q de 0 (ouverte à plat, à droite de l'écran, l'intérieur en l'air) à 1 (posée). C'est le
 * pli de la couverture vu dans un miroir, le dos du magazine sur le bord droit, et joué à
 * l'envers : le haut se pose d'abord, le coin bas gauche en dernier, et rien ne reste
 * corné. `tilt` reste l'angle qui amène l'axe des x sur `normal`, comme pour la couverture.
 */
export function closingFoldAt(q, width, height) {
  const { tilt, origin, along, normal, lift } = foldAt(1 - q, width, height, 0);
  return {
    tilt: Math.PI - tilt,
    origin: { x: width - origin.x, y: origin.y },
    along: { x: -along.x, y: along.y },
    normal: { x: -normal.x, y: normal.y },
    lift,
  };
}

// Affines au format de matrix() en CSS : [a, b, c, d, e, f], x' = a x + c y + e, y' = b x + d y + f.
const compose = (m, n) => [
  m[0] * n[0] + m[2] * n[1], m[1] * n[0] + m[3] * n[1],
  m[0] * n[2] + m[2] * n[3], m[1] * n[2] + m[3] * n[3],
  m[0] * n[4] + m[2] * n[5] + m[4], m[1] * n[4] + m[3] * n[5] + m[5],
];

/**
 * Où va le verso de la couverture, dessiné dans le repère d'une page à l'endroit :
 * retourné (le verso se lit à l'envers du recto), rabattu autour du pli, puis
 * raccourci vers le pli quand il se soulève.
 */
export function flapMatrix(fold, width) {
  const { origin: o, along: d, normal: n, lift } = fold;
  const mirror = [-1, 0, 0, 1, width, 0];
  const xx = 2 * d.x * d.x - 1, yy = 2 * d.y * d.y - 1, xy = 2 * d.x * d.y;
  const reflect = [xx, xy, xy, yy, o.x - (xx * o.x + xy * o.y), o.y - (xy * o.x + yy * o.y)];
  const k = 1 - lift, on = o.x * n.x + o.y * n.y;
  const squash = [1 - k * n.x * n.x, -k * n.x * n.y, -k * n.x * n.y, 1 - k * n.y * n.y, k * on * n.x, k * on * n.y];
  return compose(squash, compose(reflect, mirror));
}

/** Applique une affine à un point (pour les tests et les mesures). */
export const apply = (m, { x, y }) => ({ x: m[0] * x + m[2] * y + m[4], y: m[1] * x + m[3] * y + m[5] });
